import { useState } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import { specLabelKey } from '../../domain/cars/carProperties';
import {
    canCounterCall,
    canDouble,
    canVeto,
    leadableHand,
    legalPlays,
} from '../../domain/game/overtakeMatch';
import { matchTotals } from '../../domain/game/overtakeScoring';
import { cardOf, otherSide } from '../../domain/game/overtakeDeck';
import { GearIcon, InfoIcon } from '../icons/ControlIcons';
import { OvertakeBackFan } from './OvertakeCardBack';
import OvertakeHand from './OvertakeHand';
import OvertakeRound from './OvertakeRound';
import OvertakeSpecBoard from './OvertakeSpecBoard';
import OvertakeTable from './OvertakeTable';

/**
 * A match, drawn (SPEC-011 PR-11.3, PR-11.4, PR-11.5).
 *
 * The composition, in the order it is read, and it is now the order the design
 * lays a card table out in (SPEC-011 D-29): where the match stands, what the
 * rival is holding, what the screen is waiting for, THE TABLE — stock on the
 * left, the card to beat in the middle, the rival's hand on the right — then
 * your own hand, the figures you may call, and the three powers.
 *
 * THE PROMPT IS A STRIP ABOVE THE TABLE. It has been in three places now, and
 * the argument each time was about which thing it is the instruction for. It is
 * the instruction for the SCREEN — what is being waited on, whoever is waiting —
 * so it sits where the table starts rather than beside the hand or under it,
 * and it takes a glyph and a rule so it reads as a note about the game and not
 * as a line of the game's own copy. It is `aria-live`, so nothing about the
 * announcement depends on where it is.
 *
 * THE PICKER IS UNDER THE HAND, not over it, which reverses PR-11.3. A hand you
 * choose FROM and a figure you call WITH are two taps of one move, and the move
 * runs downwards: the cards are the sentence's subject and the figure is its
 * verb. It also puts the picker and the three powers — everything that is a
 * control rather than a card — inside one reach of the thumb.
 *
 * It holds no rule. Every question about what may happen is asked of
 * `domain/game` (D-15), and its own state is exactly two fields — the card
 * picked up but not yet committed, and whether the reader means to double — both
 * of which exist only because leading is ONE move collected in TWO taps.
 *
 * WHICH SIDE THE READER IS is a parameter. In a local match it is always
 * `player`; across two devices one state is read from two directions, so the
 * sides in it are absolute and this component has to be told which one belongs
 * to the person holding the phone.
 */
export default function OvertakeBoard({
    match,
    deck,
    mySide = 'player',
    isWaiting = false,
    onMove,
    onAgain,
    onLeave,
}) {
    const t = useTranslate();
    const [picked, setPicked] = useState(null);
    const [wantsDouble, setWantsDouble] = useState(false);
    const rival = otherSide(mySide);
    const isMine = match.turn === mySide;
    const totals = matchTotals(match, deck);
    const isLeading = match.status === 'leading' && isMine;
    const isAnswering = match.status === 'answering' && isMine;
    const isRenaming = match.status === 'renaming' && isMine;
    const isSettled = match.status === 'roundOver' || match.status === 'over';
    // Which of the reader's cards may be touched depends entirely on what the
    // match is waiting for, and both answers come from the domain.
    const playable = (() => {
        if (isLeading) {
            return leadableHand(match, deck);
        }

        return isAnswering ? legalPlays(match, deck) : [];
    })();

    const commit = (played) => {
        setPicked(null);
        setWantsDouble(false);
        onMove(played);
    };

    const name = (spec) => {
        if (isRenaming) {
            commit({ type: 'rename', spec });

            return;
        }

        commit({ type: 'lead', carId: picked, spec, doubled: wantsDouble });
    };

    return (
        <>
            <header className="overtake-score">
                <p className="overtake-score__side overtake-score__side--you">
                    <span className="overtake-score__who">{t('play.you')}</span>
                    <span className="overtake-score__points figure">{totals[mySide]}</span>
                </p>

                {/* The round, and the figure it is being played for. The figure
                    lives here as well as under the standing card because this is
                    the line a reader checks without looking down — but only the
                    caption plate carries `overtake-table__figure`, so there is
                    exactly one of that on the page. */}
                <p className="overtake-score__plate">
                    <span className="overtake-score__round">{t('play.round', { count: match.round })}</span>
                    <span className={`overtake-score__figure${match.spec ? '' : ' overtake-score__figure--open'}`}>
                        {match.spec ? (
                            <>
                                {/* The DIRECTION rides with it, and it is not
                                    decoration: a counter-call inverts the
                                    comparison (D-7), and a line still reading
                                    "top speed" after somebody turned it over
                                    would be the screen stating the wrong rule.
                                    It sits here rather than under the card
                                    because this is the line a reader checks
                                    without looking down. */}
                                <span className="overtake-score__direction">
                                    {t(`play.direction.${match.direction}`)}
                                </span>
                                {' '}
                                {t(specLabelKey(match.spec))}
                            </>
                        ) : t('play.table.open')}
                    </span>
                </p>

                <p className="overtake-score__side overtake-score__side--rival">
                    <span className="overtake-score__who">{t('play.rival')}</span>
                    <span className="overtake-score__points figure">{totals[rival]}</span>
                </p>

                {/* The way out of a match, and the only control on this screen
                    that is not a card. It was reachable only once a match had
                    ENDED, which meant a reader who wanted to change the one they
                    were in had to lose it first. A cog rather than a word,
                    because it acts on the match rather than on the round.

                    Not drawn once the match IS over: the band under the pair
                    offers the same way out as a full control by then, and two
                    controls with one name is one of them too many. */}
                {match.status === 'over' ? null : (
                    <button
                        type="button"
                        className="overtake-score__leave"
                        onClick={onLeave}
                        aria-label={t('play.leave')}
                    >
                        <GearIcon size={18} />
                    </button>
                )}
            </header>

            {/* What the other side is holding. Not secret, and never
                previously shown — the difference between an opponent who can
                still answer and one who is about to pass. */}
            <OvertakeBackFan count={match.hands[rival].length} label={t('play.cards')} />

            {/* What the screen is waiting for, in one line, above the table it
                is about. `aria-live` because a reader using a screen reader
                would otherwise have to go looking for the reason nothing
                responded to their tap. */}
            <p className="overtake-prompt" aria-live="polite">
                <InfoIcon className="overtake-prompt__mark" size={18} />
                <span className="overtake-prompt__line">
                    {t(promptKey({
                        match, isMine, isLeading, isRenaming, picked,
                    }))}
                </span>
            </p>

            <OvertakeTable
                match={match}
                deck={deck}
                mySide={mySide}
                rivalCards={match.hands[rival].length}
            />

            {isSettled ? (
                <OvertakeRound
                    match={match}
                    deck={deck}
                    mySide={mySide}
                    onNext={() => commit({ type: 'advance' })}
                    onAgain={() => {
                        setPicked(null);
                        setWantsDouble(false);
                        onAgain();
                    }}
                    onLeave={onLeave}
                />
            ) : null}

            {match.status === 'over' ? null : (
                <>
                    <OvertakeHand
                        ids={match.hands[mySide]}
                        deck={deck}
                        spec={match.spec}
                        playable={playable}
                        selected={picked}
                        onSelect={(id) => (isAnswering
                            ? commit({ type: 'play', carId: id })
                            : setPicked(id === picked ? null : id))}
                        isDisabled={isWaiting || !isMine || isSettled}
                    />

                    {/* Directly under the hand, because picking a card is what
                        lights it up and calling the figure is the same move's
                        second tap. */}
                    <OvertakeSpecBoard
                        spent={match.spent}
                        inPlay={match.spec}
                        direction={match.direction}
                        vetoed={match.vetoed}
                        card={picked ? cardOf(deck, picked) : null}
                        onName={(isLeading && picked) || isRenaming ? name : undefined}
                    />

                    {/* THE THREE POWERS, ALWAYS DRAWN. Each is a real decision
                        rather than a button, and each was previously rendered
                        only while it was available — which made the row appear,
                        change width and vanish under the thumb that was reaching
                        for it. They are a fixed row now: a power that has been
                        spent, or that this moment does not offer, is drawn
                        unavailable and says which of the two it is. That is also
                        the record of what the match has cost, in the one place a
                        reader would look for it.

                        `aria-disabled` rather than `disabled`, for the reason
                        the cards give: a disabled element drops focus on the
                        spot in several engines. */}
                    <div className="overtake-actions" role="group" aria-label={t('play.powers')}>
                        <Power
                            title={t(isAnswering || isRenaming ? 'play.counter' : 'play.double')}
                            note={t(isAnswering || isRenaming ? 'play.counter.note' : 'play.double.note')}
                            left={powerLeft(match, mySide, isAnswering || isRenaming ? 'counter' : 'double')}
                            isOffered={isAnswering ? canCounterCall(match) : isLeading && canDouble(match)}
                            isArmed={wantsDouble && isLeading}
                            onUse={isAnswering
                                ? () => commit({ type: 'counter' })
                                : () => setWantsDouble((armed) => !armed)}
                        />

                        <Power
                            title={t('play.veto')}
                            note={t('play.veto.note')}
                            left={powerLeft(match, mySide, 'veto')}
                            isOffered={isAnswering && canVeto(match)}
                            onUse={() => commit({ type: 'veto' })}
                        />

                        <Power
                            title={t('play.pass')}
                            note={t('play.pass.note')}
                            isOffered={isAnswering || isRenaming}
                            onUse={() => commit({ type: 'pass' })}
                        />
                    </div>
                </>
            )}
        </>
    );
}

/**
 * One of the three powers, as a control that is always there.
 *
 * TITLE, NOTE AND WHAT IS LEFT. The verb alone assumes a reader who already
 * knows this game — "refuse it" is not a rule — so each carries the sentence
 * under it, and the ones that are spent for good carry the count that says so.
 * `left` is omitted for PASS, which is not a power and cannot run out.
 *
 * Drawn unavailable rather than removed. A row that appears and disappears
 * changes width under a thumb already reaching for it, and the fact that a
 * power has been spent is worth as much on screen as the fact that it is there.
 */
function Power({
    title,
    note,
    left = null,
    isOffered,
    isArmed = false,
    onUse,
}) {
    const t = useTranslate();
    const isSpent = left === 0;

    return (
        <button
            type="button"
            className={[
                'overtake-power',
                isOffered ? '' : 'is-quiet',
                isSpent ? 'is-spent' : '',
                isArmed ? 'is-armed' : '',
            ].filter(Boolean).join(' ')}
            onClick={() => isOffered && onUse()}
            aria-disabled={isOffered ? undefined : 'true'}
            aria-pressed={isArmed || undefined}
        >
            <span className="overtake-power__title">{title}</span>
            <span className="overtake-power__note">{isSpent ? t('play.powers.spent') : note}</span>
            {left === null ? null : (
                <span className="overtake-power__left figure" aria-hidden="true">{left}</span>
            )}
        </button>
    );
}

/** How many times this side may still use a power: one, or none. */
function powerLeft(match, side, power) {
    return match?.powers?.[side]?.[power] ? 1 : 0;
}

/**
 * The one instruction on the screen.
 *
 * A function rather than a chain of ternaries in the JSX, because the order of
 * these tests IS the rule about what the reader is being asked, and it should be
 * readable as a list rather than as indentation.
 */
function promptKey({ match, isMine, isLeading, isRenaming, picked }) {
    if (match.status === 'roundOver' || match.status === 'over') {
        return 'play.prompt.result';
    }

    if (!isMine) {
        return 'play.prompt.waiting';
    }

    if (isRenaming) {
        return 'play.prompt.rename';
    }

    if (isLeading) {
        return picked ? 'play.prompt.name' : 'play.prompt.lead';
    }

    return 'play.prompt.answer';
}
